import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { useNavigate } from 'react-router-dom'
import {
  MyPurchaseRequestsDataCollectionDocument,
  type MyPurchaseRequestItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import {
  getApprovalProcessStatus,
  getPurchaseRequestStatusLabel,
} from 'modules/procurement/components/purchaseRequests/SpendingRequestStatusLabel'
import { purchaseRequestDisplayTitle } from 'modules/procurement/lib/purchaseRequestTitle'

type StatusType = 'positive' | 'critical' | 'warning' | 'neutral' | 'info'

const mapPrStatusToDataCollection = (type: string): StatusType => {
  const mapping: Record<string, StatusType> = {
    positive: 'positive',
    critical: 'critical',
    warning: 'warning',
  }
  return mapping[type] ?? 'neutral'
}

const MyPurchaseRequestsDataTable = () => {
  const {
    employee,
    company,
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const source = useGraphqlDataCollectionSource({
    document: MyPurchaseRequestsDataCollectionDocument,
    perPage: 30,
    mapData: (data) =>
      data.procurement.purchaseRequestsConnection.edges.map((edge) => edge.node) ?? [],
    mapPagination: (data) => ({
      total: data.procurement.purchaseRequestsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings }) => ({
      companyId: company.id,
      requesterEmployeeIds: [employee.id],
      sortOrder: sortings?.[0] ?? { field: 'date', order: 'desc' },
    }),
    sortings: {
      description: { label: i18n.t('procurement.purchase_requests.list.description') },
      status: { label: i18n.t('procurement.purchase_requests.list.status') },
      date: { label: i18n.t('procurement.purchase_requests.list.requested') },
      deadline: { label: i18n.t('procurement.purchase_requests.list.deadline') },
    },
    defaultSortings: { field: 'date', order: 'desc' },
    itemUrl: (item: MyPurchaseRequestItemFragment) =>
      navigation.myPurchases.detail.fullPath({ id: item.id }),
    primaryActions: () => ({
      label: i18n.t('procurement.purchase_requests.actions.new_request'),
      icon: Plus,
      onClick: () => navigate(navigation.myPurchases.newRequest.fullPath()),
    }),
  })

  return (
    <OneDataCollection
      id="procurement-my-purchase-requests/v1"
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnHiding: true,
            columns: [
              {
                id: 'description',
                label: i18n.t('procurement.purchase_requests.list.description'),
                sorting: 'description',
                noHiding: true,
                render: (item: MyPurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: { text: purchaseRequestDisplayTitle(item) },
                }),
              },
              {
                id: 'status',
                label: i18n.t('procurement.purchase_requests.list.status'),
                sorting: 'status',
                render: (item: MyPurchaseRequestItemFragment) => {
                  const resolvedStatus = getApprovalProcessStatus({
                    status: item.status,
                    approvalProcess: item.approvalProcess,
                  })
                  const statusInfo = getPurchaseRequestStatusLabel(resolvedStatus)
                  return {
                    type: 'status' as const,
                    value: {
                      label: statusInfo.label,
                      status: mapPrStatusToDataCollection(statusInfo.type),
                    },
                  }
                },
              },
              {
                id: 'cost',
                label: i18n.t('procurement.purchase_requests.list.cost'),
                align: 'right' as const,
                render: (item: MyPurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: {
                    text: item.cost
                      ? MoneyWithCurrency({
                          amount: Number(item.cost.cents),
                          currency: item.cost.currency,
                          locale,
                        })
                      : '',
                  },
                }),
              },
              {
                id: 'date',
                label: i18n.t('procurement.purchase_requests.list.requested'),
                sorting: 'date',
                render: (item: MyPurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: { text: dateBackendDateTimeToUI(item.date) },
                }),
              },
              {
                id: 'deadline',
                label: i18n.t('procurement.purchase_requests.list.deadline'),
                sorting: 'deadline',
                render: (item: MyPurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: {
                    text: item.deadline ? dateBackendDateTimeToUI(item.deadline) : '-',
                  },
                }),
              },
              {
                id: 'vendor',
                label: i18n.t('procurement.purchase_requests.list.vendor'),
                render: (item: MyPurchaseRequestItemFragment) => ({
                  type: 'text' as const,
                  value: { text: contactFullName(item.vendor) },
                }),
              },
            ],
            frozenColumns: 1 as const,
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('procurement.purchase_requests.empty.description_employee'),
        },
      }}
    />
  )
}

export default MyPurchaseRequestsDataTable
